import DOMPurify from 'dompurify'
import { ErrorMessage, useFormikContext } from 'formik'
import React, { useMemo } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { townsSelector } from '../store/createApplicationSlice'
import { RootState } from '../store/rootReducer'
import { CreateApplicationFormValues } from './CreateApplication'

export function hasDisclaimerContent(html: string | undefined): boolean {
  if (!html) return false
  const div = document.createElement('div')
  div.innerHTML = html
  return !!div.textContent?.trim()
}

const CreateApplicationDisclaimer = (): React.ReactElement | null => {
  const { t, i18n } = useTranslation('createApplication')
  const { values, getFieldProps } = useFormikContext<CreateApplicationFormValues>()
  const selectedTown = useSelector((state: RootState) => townsSelector.selectById(state, values.town || -1))

  const safeHtml = useMemo(() => {
    if (!selectedTown) return ''
    const raw =
      i18n.language === 'en' && hasDisclaimerContent(selectedTown.disclaimerTextEn)
        ? selectedTown.disclaimerTextEn
        : selectedTown.disclaimerText
    return hasDisclaimerContent(raw) ? DOMPurify.sanitize(raw, { FORBID_ATTR: ['style'] }) : ''
  }, [selectedTown, i18n.language])

  if (!safeHtml) {
    return null
  }

  return (
    <Card className="mb-2">
      <Card.Body>
        <Row>
          <Col>
            <h4>{t('disclaimer.title')}</h4>
          </Col>
        </Row>
        <Row>
          <Col>
            <div
              className="mb-3"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group controlId="disclaimerAccepted">
              <Form.Check
                type="checkbox"
                label={t('disclaimer.accept')}
                {...getFieldProps('disclaimerAccepted')}
                checked={values.disclaimerAccepted}
              />
              <ErrorMessage name="disclaimerAccepted">
                {(msg) => <div className="text-danger small mt-1">{msg}</div>}
              </ErrorMessage>
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

export default CreateApplicationDisclaimer
